/**
 * Composition point — the single place that wires CacheClient and TileRegistry.
 *
 * The rest of the app imports only `appCache` and `appRegistry` from here.
 *
 * Dev builds (`import.meta.env.DEV`) get the seeded mock cache + fixture
 * adapters; production builds get the real HTTP client + real adapters. With
 * DEV=false the dev branch is dead code, so Rollup eliminates the entire dev
 * subgraph (mock cache, fixtures) from the production bundle —
 * `scripts/verify-clean-bundle.mjs` fails the build if it ever leaks back in.
 */
import { buildRegistry } from './registry.js';
import { makeProdComposition, type Composition } from './prod-composition.js';
import { makeDevComposition } from './dev-composition.js';

// VITE_REAL_DATA=1 flips dev onto the production wiring (real HTTP client →
// the local /api/tile middleware in vite.config.ts) for live-data testing.
// Prod builds are unaffected: DEV=false short-circuits to prod wiring and the
// dev subgraph still dead-code-eliminates (verify-clean-bundle enforces).
const composition: Composition =
  import.meta.env.DEV && import.meta.env['VITE_REAL_DATA'] !== '1'
    ? makeDevComposition()
    : makeProdComposition();

/** The app-wide CacheClient. */
export const appCache = composition.cache;

/** The app-wide TileRegistry. */
export const appRegistry = buildRegistry(composition.adapters);
